import { formatifyHtml } from "./formatifyHtml.js";
import { thumbFromUrl } from "./thumbFromUrl.js";

function convertImagesToPicture(html: string, lightboxId = "content"): string {
	return html.replace(
		/<img(?<p1>[^>]+)src="(?<src>[^"]+)"(?<p2>[^>]*)>/gu,
		(_match, p1: string, src: string, p2: string) => {
			return `
				<picture class="js-picture">
					<source
						srcset="${thumbFromUrl(540, 0, src)} 1x"
						type="image/webp"
						media="(min-width: 577px)"
					/>
					<source
						srcset="${thumbFromUrl(530, 0, src)} 1x"
						type="image/webp"
						media="(max-width: 576px)"
					/>
					<a href="${thumbFromUrl(1440, 0, src)}" data-fslightbox="${lightboxId}" data-type="image">
						<img${p1}src="${thumbFromUrl(636, 0, src)}"${p2}>
					</a>
				</picture>
			`;
		},
	);
}

/**
 * Tables with a header and at most this many columns collapse into stacked cards on mobile;
 * wider tables (or headerless ones) fall back to whole-surface horizontal scrolling instead.
 */
const MAX_STACK_COLUMNS = 4;

/**
 * Body cells whose text is at most this long keep on a single line (numbers, short
 * labels like "1–2 týdny" or "30 000 Kč"), so wide tables read cleanly. Longer text
 * is allowed to wrap so a single big paragraph can't blow the table out horizontally.
 */
const MAX_NOWRAP_CHARS = 25;

function getColumnCount(table: HTMLTableElement): number {
	let max = 0;
	for (const row of Array.from(table.rows)) {
		let count = 0;
		for (const cell of Array.from(row.cells)) {
			count += cell.colSpan || 1;
		}
		max = Math.max(max, count);
	}
	return max;
}

/** Header labels expanded by colSpan so they align with body columns by index. */
function getHeaderLabels(table: HTMLTableElement): string[] {
	const headerRow = table.tHead?.rows[0];
	if (!headerRow) {
		return [];
	}
	const labels: string[] = [];
	for (const cell of Array.from(headerRow.cells)) {
		const text = (cell.textContent ?? "").trim();
		for (let span = cell.colSpan || 1; span > 0; span--) {
			labels.push(text);
		}
	}
	return labels;
}

/** Adds data-label (column header text) to each body cell so cards can render labels on mobile. */
function injectDataLabels(table: HTMLTableElement, labels: string[]): void {
	for (const body of Array.from(table.tBodies)) {
		for (const row of Array.from(body.rows)) {
			let column = 0;
			for (const cell of Array.from(row.cells)) {
				const label = labels[column];
				if (cell.tagName === "TD" && label) {
					cell.setAttribute("data-label", label);
				}
				column += cell.colSpan || 1;
			}
		}
	}
}

/** Keeps short, label-like body cells on one line while letting long text wrap. */
function markNoWrapCells(table: HTMLTableElement): void {
	for (const body of Array.from(table.tBodies)) {
		for (const row of Array.from(body.rows)) {
			for (const cell of Array.from(row.cells)) {
				if (cell.tagName !== "TD") {
					continue;
				}
				const text = (cell.textContent ?? "").trim();
				const hasLineBreak = cell.querySelector("br") !== null;
				if (text.length > 0 && text.length <= MAX_NOWRAP_CHARS && !hasLineBreak) {
					cell.classList.add("responsive-table__nowrap");
				}
			}
		}
	}
}

function processTables(root: HTMLElement): void {
	for (const table of Array.from(root.querySelectorAll("table"))) {
		const labels = getHeaderLabels(table);
		const hasUsableHeader = labels.some((label) => label.length > 0);
		const stack = hasUsableHeader && getColumnCount(table) <= MAX_STACK_COLUMNS;

		if (stack) {
			injectDataLabels(table, labels);
		}

		markNoWrapCells(table);

		const wrapper = document.createElement("div");
		wrapper.className = `responsive-table ${stack ? "responsive-table--stack" : "responsive-table--scroll"}`;
		table.parentNode?.insertBefore(wrapper, table);
		wrapper.appendChild(table);
	}
}

function fixYoutubeIframes(html: string): string {
	return html.replace(
		/<iframe(?<p1>[^>]+)src="https:[/][/]www[.]youtube[.]com[/]embed[/](?<v>[^"]+)"(?<p2>[^>]*)>/gu,
		(_match, p1: string, v: string, p2: string) =>
			`<iframe ${p1} title="YouTube video" src="https://www.youtube.com/embed/${v}" referrerpolicy="strict-origin-when-cross-origin" ${p2}>`,
	);
}

export function transformCkEditorContent(
	html: string,
	options?: { customClass?: string; lightboxId?: string },
): string {
	const { customClass, lightboxId } = options ?? {};
	const withConvertedImages = convertImagesToPicture(html, lightboxId);
	const withFixedYoutubeIframes = fixYoutubeIframes(withConvertedImages);
	const htmlElement = document.createElement("div");
	htmlElement.innerHTML = withFixedYoutubeIframes;
	processTables(htmlElement);
	const withFormatted = formatifyHtml(htmlElement);
	return `<div class="ckeditor-content ${customClass ?? ""}">${withFormatted}</div>`;
}
